<template>
  <DialogLayout class-name="contact-dialog" @close="emit('close')">
    <template #title>{{ t('chat.contactTitle') }}</template>

    <template #subTitle>{{ t('chat.contactSubtitle') }}</template>

    <div class="contact-body">
      <!-- 左侧：公司列表 -->
      <div class="company-list">
        <div
          v-for="company in companies"
          :key="company.id"
          class="company-item"
          :class="{ active: selectedCompanyId === company.id }"
          @click="handleSelectCompany(company)"
        >
          <div class="company-logo">
            <img src="@/assets/images/default-icon.png" :alt="company.name" />
          </div>
          <span class="company-name">{{ company.name }}</span>
        </div>
        <div v-if="!companies.length && !companiesLoading" class="contact-empty">
          <EmptyState type="empty" :title="t('chat.noCompanies')" />
        </div>
        <div v-if="companiesLoading" class="contact-loading">
          <el-icon class="is-loading"><Loading /></el-icon>
        </div>
      </div>

      <!-- 分割线 -->
      <div class="contact-divider" />

      <!-- 右侧：人员列表 -->
      <div class="person-list">
        <template v-if="selectedCompanyId">
          <div
            v-for="person in persons"
            :key="person.id"
            class="person-item"
            :class="{ opening: openingPersonId === person.id }"
            @click="handleSelectPerson(person)"
          >
            <div class="person-avatar">
              <img v-if="person.avatar" :src="person.avatar" :alt="person.name" />
              <div v-else class="avatar-placeholder">
                {{ (person.name || '?')[0].toUpperCase() }}
              </div>
            </div>
            <span class="person-name">{{ person.name }}</span>
            <el-icon v-if="openingPersonId === person.id" class="person-chat-icon is-loading"><Loading /></el-icon>
            <el-icon v-else class="person-chat-icon"><ChatDotRound /></el-icon>
          </div>
          <div v-if="!persons.length && !personsLoading" class="contact-empty">
            <EmptyState type="empty" :title="t('common.noData')" />
          </div>
          <div v-if="personsLoading" class="contact-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
          </div>
        </template>
        <div v-else class="contact-empty">
          <div class="contact-empty-title">{{ t('chat.selectCompanyFirst') }}</div>
        </div>
      </div>
    </div>
  </DialogLayout>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Loading, ChatDotRound } from '@element-plus/icons-vue'
import DialogLayout from './dialog-layout.vue'
import { EmptyState } from '@/components/basic-components/empty-state'
import type { ContactDialogProps, ContactCompany, ContactPerson } from './contact-dialog-types'
import { useChatStore } from '@/store/modules/chat'
import { getHeadhuntingListApi, getEnterprisListApi, getEnterpriseAccountListApi } from '@/api/enterprise'
import { System } from '@/router/config/roles';
import { useUserStore } from '@/store/modules/user';

const user = useUserStore();
const isBiz = computed(() => {
  return user.userSystem == System.business
})
const { t } = useI18n()
const props = withDefaults(defineProps<ContactDialogProps>(), {
  companies: () => [],
})
const emit = defineEmits<{
  (e: 'confirm', person: ContactPerson): void
  (e: 'cancel'): void
  (e: 'close'): void
}>()

const chatStore = useChatStore()

const companiesLoading = ref(false)
const personsLoading = ref(false)
const openingPersonId = ref<string | number | null>(null)
const companies = ref<ContactCompany[]>([...props.companies])
const persons = ref<ContactPerson[]>([])
const selectedCompanyId = ref<string | number | null>(null)

// ─────────────────────────────────────────────────────────────────────────────

async function fetchCompanies() {
  if (props.companies.length) {
    companies.value = [...props.companies]
    return
  }
  companiesLoading.value = true
  try {
    companies.value = isBiz.value ? await getHeadhuntingListApi() : await getEnterprisListApi()
  } finally {
    companiesLoading.value = false
  }
}

async function fetchPersons(companyId: string | number) {
  personsLoading.value = true
  persons.value = []
  try {
    const companyName = companies.value.find(c => c.id === companyId)?.name
    const list = await getEnterpriseAccountListApi(companyId, { p: isBiz.value ? 3 : 2 })
    persons.value = (list ?? [])
      .filter(item => item?.id !== undefined && item?.id !== null)
      .map((item) => {
        const id = item.id as string | number
        return {
          id,
          name: item.name || item.email || String(id),
          avatar: undefined,
          chatUserId: `LINC-CHAT-${id}`,
          companyId,
          companyName,
        } as ContactPerson
      })
  } finally {
    personsLoading.value = false
  }
}

function handleSelectCompany(company: ContactCompany) {
  selectedCompanyId.value = company.id
  fetchPersons(company.id)
}

async function handleSelectPerson(person: ContactPerson) {
  if (openingPersonId.value) return
  openingPersonId.value = person.id
  try {
    await chatStore.openDirectChannel(person.chatUserId)
    emit('confirm', person)
  } finally {
    openingPersonId.value = null
  }
}

watch(() => props.companies, (val) => {
  if (val?.length) companies.value = [...val]
}, { immediate: false })

fetchCompanies()
</script>

<style lang="scss">
.contact-dialog {
  width: 830px;

  .dialog-content {
    padding-bottom: 0;
  }
}
</style>

<style scoped lang="scss">

.contact-body {
  display: flex;
  gap: 0;
  height: 420px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
}

.contact-divider {
  width: 1px;
  background: #e4e7ed;
  flex-shrink: 0;
}

// ─── 公司列表 ───────────────────────────────────────────────────────────────
.company-list {
  width: 340px;
  flex-shrink: 0;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.company-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #8755DE;
    background: #F4EFFD;
  }

  &.active {
    border-color: #8755DE;
    background: #F4EFFD;
  }
}

.company-logo {
  width: 40px;
  height: 43px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.company-logo-placeholder {
  font-size: 18px;
  color: #86909c;
}

.company-name {
  font-size: 13px;
  color: #212429;
  line-height: 1.4;
  word-break: break-all;
}

// ─── 人员列表 ───────────────────────────────────────────────────────────────
.person-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.person-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f0f2f5;

  &:last-child {
    border-bottom: none;
  }

  &.opening {
    background: #f5f0ff;
    cursor: wait;
  }

  &:hover {
    background: #f5f0ff;

    .person-chat-icon {
      opacity: 1;
    }
  }
}

.person-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.avatar-placeholder {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #8755DE;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.person-name {
  flex: 1;
  font-size: 14px;
  color: #212429;
}

.person-chat-icon {
  font-size: 18px;
  color: #8755DE;
  opacity: 0;
  transition: opacity 0.15s;
}

// ─── 空状态 / 加载 ───────────────────────────────────────────────────────────
.contact-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  .contact-empty-title {
    font-size: 14px;
    font-weight: normal;
    line-height: 20px;
    color: #C9D3D9;
  }
}

.contact-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #8755DE;
}

</style>
